import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Clock, ListChecks, ShieldCheck, Gauge } from "lucide-react";
import { BookFitCall } from "@/components/site/BookFitCall";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, faqPageSchema, webPageSchema, scanAppSchema, SCAN_APP_ID } from "@/lib/seo";
import { scanUrl, SCAN_QUESTION_COUNT, SCAN_DURATION_MINUTES } from "@/lib/diagnostic";
import { SixPillars } from "@/components/diagrams/SixPillars";

const TITLE = "Free Business Operations Health Check | Ajility";
const OG_TITLE = "Free business operations health check: the Operating Scan";
const DESCRIPTION =
  "A free business operations health check for founder-led Australian businesses: 21 plain statements, about six minutes, no email needed to see the result.";

const faqs = pageFaqAdditions["/scan"];

export const Route = createFileRoute("/scan")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: OG_TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: OG_TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("tools", "Operating Scan by Ajility"),
      ogUrl("/scan"),
    ],
    links: canonicalLinks("/scan"),
    scripts: [
      jsonLd(faqPageSchema("/scan", faqs, SCAN_APP_ID)),
      jsonLd(breadcrumbSchema([{ name: "Operating Scan", path: "/scan" }])),
      jsonLd(scanAppSchema()),
      jsonLd(
        webPageSchema({ image: "tools", mainEntityId: SCAN_APP_ID,
          path: "/scan",
          name: TITLE,
          description: DESCRIPTION,
        }),
      ),
    ],
  }),
  component: ScanPage,
});

const facts = [
  { icon: ListChecks, t: `${SCAN_QUESTION_COUNT} plain statements`, d: "Each statement describes something you can recognise in how the business runs. Answer from Not us to Always, with no preparation needed." },
  { icon: Clock, t: `About ${SCAN_DURATION_MINUTES} minutes`, d: "Complete the statements in one sitting, or stop and return when it suits you." },
  { icon: Gauge, t: "A provisional score", d: "You see the weakest areas and an overall score. It is provisional because it rests on your answers, not evidence Ajility has examined." },
  { icon: ShieldCheck, t: "A conversation if you want one", d: "The result is yours with no call required. If you want to talk it through, the next conversation starts from your score." },
];

function ScanPage() {
  return (
    <>
      <section className="bg-midnight pb-20 pt-32 text-frost">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <span className="eyebrow mb-3 block text-steel-on-dark">Start free</span>
            <h1 className="font-serif text-4xl leading-tight sm:text-5xl">Operating Scan</h1>
            <p className="mt-6 text-lg leading-relaxed text-frost/80">
              A free business operations health check for founder-led Australian businesses. 21 plain statements about how the business actually runs, answered from Not us to Always. About six minutes, then a provisional score naming the weakest areas and a conversation if you want one. If you already know the decision you need to make, the diagnostic tells you whether you are right and gives you something to show the team.
            </p>
            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row">
              <a
                href={scanUrl("scan_page")}
                data-analytics-cta="Take the scan"
                data-analytics-location="scan_page"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight-deep transition hover:bg-white"
              >
                Take the scan
              </a>
              <BookFitCall
                placement="scan_page_hero"
                buttonClassName="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 px-7 text-base font-semibold text-frost transition hover:border-steel hover:text-frost"
                noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
              >
                Book a fit call
              </BookFitCall>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-frost/85">
              Booked a call already? Take the Scan first and the call starts from your score.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-4 font-serif text-3xl text-midnight sm:text-4xl">Operating Scan statements and score</h2>
          <p className="mb-10 max-w-3xl leading-relaxed text-charcoal">
            You read each statement, choose the answer that sounds most like your business and see which areas need attention first.
          </p>
          <div className="mb-14">
            <SixPillars />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            {facts.map((f) => (
              <div key={f.t} className="rounded-3xl border border-border bg-card p-8 shadow-soft">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-midnight text-steel-on-dark">
                  <f.icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-midnight">{f.t}</h3>
                <p className="mt-3 leading-relaxed text-charcoal">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFaqs faqs={faqs} />

      <section className="bg-card py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="font-serif text-3xl text-midnight sm:text-4xl">How confident the score is</h2>
          <p className="mt-6 leading-relaxed text-charcoal">
            The scan reads your view of the business. It is a screening instrument, not an audit.
            21 statements can place an area as strong, mixed or weak with reasonable
            confidence. They cannot tell you why, and they cannot see what you have not noticed.
            Treat the result as a direction, not a finding.
          </p>
          <p className="mt-4 leading-relaxed text-charcoal">
            Where a score matters commercially, the next step is the Operating Diagnostic
            from $3,600 plus GST. It tests the same areas against evidence, documents and
            interviews rather than self-report.
          </p>
          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row">
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-frost transition hover:bg-midnight-deep"
            >
              See what comes after the scan
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <BookFitCall
              placement="scan_page_after_score"
              buttonClassName="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-midnight transition hover:bg-mist"
              noteClassName="mt-2 text-sm font-medium text-charcoal/80 hover:text-midnight"
            >
              Book a fit call
            </BookFitCall>
          </div>
        </div>
      </section>
    </>
  );
}
